import { Injectable } from '@nestjs/common';
import * as bcrypt from 'bcrypt';
import { randomUUID } from 'crypto';
import { PrismaService } from '@cart/api-data-access';
import { AuthUser, ThemeMode, UpdateProfileRequest, UserRole } from '@cart/shared-types';
import { StoreContextService } from '../tenancy/store-context.service';

export interface StoredUser extends AuthUser {
  passwordHash: string;
  // Raw FK, not part of the public AuthUser shape — AuthService.toAuthUser()
  // uses this to enrich organizationName/organizationApprovalStatus/
  // organizationRejectionReason before the response goes out.
  organizationId?: string;
}

export interface CreateUserInput {
  email: string;
  passwordHash: string;
  name: string;
  role?: UserRole;
  organizationId?: string;
}

export interface CreateGoogleUserInput {
  email: string;
  name: string;
  googleId: string;
}

export abstract class AuthRepository {
  abstract list(): Promise<StoredUser[]>;
  abstract create(input: CreateUserInput): Promise<StoredUser>;
  abstract findByEmail(email: string): Promise<StoredUser | undefined>;
  abstract findById(id: string): Promise<StoredUser | undefined>;
  abstract updateProfile(id: string, input: UpdateProfileRequest): Promise<StoredUser | undefined>;
  abstract updateThemeMode(id: string, themeMode: ThemeMode): Promise<StoredUser | undefined>;
  abstract findByGoogleId(googleId: string): Promise<StoredUser | undefined>;
  abstract createWithGoogle(input: CreateGoogleUserInput): Promise<StoredUser>;
  abstract linkGoogleId(id: string, googleId: string): Promise<StoredUser>;
  abstract setResetToken(userId: string, resetTokenHash: string, expiresAt: Date): Promise<void>;
  abstract findByResetTokenHash(resetTokenHash: string): Promise<StoredUser | undefined>;
  abstract resetPassword(userId: string, passwordHash: string): Promise<void>;
  abstract setEmailVerificationToken(userId: string, tokenHash: string, expiresAt: Date): Promise<void>;
  abstract findByEmailVerificationTokenHash(tokenHash: string): Promise<StoredUser | undefined>;
  abstract markEmailVerified(userId: string): Promise<void>;
}

@Injectable()
export class PrismaAuthRepository implements AuthRepository {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext?: StoreContextService
  ) {}

  async list(): Promise<StoredUser[]> {
    const users = await this.prisma.user.findMany({
      where: { storeId: this.storeId() },
      orderBy: { createdAt: 'desc' },
    });

    return users.map((user) => this.toStoredUser(user));
  }

  async create(input: CreateUserInput): Promise<StoredUser> {
    const user = await this.prisma.user.create({
      data: {
        email: input.email,
        storeId: this.storeId(),
        passwordHash: input.passwordHash,
        name: input.name,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        role: (input.role ?? 'customer') as any,
        organizationId: input.organizationId,
      },
    });

    return this.toStoredUser(user);
  }

  async findByEmail(email: string): Promise<StoredUser | undefined> {
    const user = await this.prisma.user.findUnique({ where: { email } });
    return user ? this.toStoredUser(user) : undefined;
  }

  async findById(id: string): Promise<StoredUser | undefined> {
    const user =
      (await this.prisma.user.findFirst({ where: { id, storeId: this.storeId() } })) ??
      (await this.prisma.user.findFirst({ where: { id } }));
    return user ? this.toStoredUser(user) : undefined;
  }

  async updateProfile(id: string, input: UpdateProfileRequest): Promise<StoredUser | undefined> {
    const existing = await this.prisma.user.findFirst({
      where: { id, storeId: this.storeId() },
      select: { id: true },
    });

    if (!existing) {
      return undefined;
    }

    const user = await this.prisma.user.update({
      where: { id },
      data: {
        name: input.name,
        email: input.email,
      },
    });

    return this.toStoredUser(user);
  }

  async updateThemeMode(id: string, themeMode: ThemeMode): Promise<StoredUser | undefined> {
    const existing = await this.prisma.user.findFirst({
      where: { id, storeId: this.storeId() },
      select: { id: true },
    });

    if (!existing) {
      return undefined;
    }

    const user = await this.prisma.user.update({
      where: { id },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: { themeMode: themeMode as any },
    });

    return this.toStoredUser(user);
  }

  async findByGoogleId(googleId: string): Promise<StoredUser | undefined> {
    const user = await this.prisma.user.findUnique({ where: { googleId } });
    return user ? this.toStoredUser(user) : undefined;
  }

  async createWithGoogle(input: CreateGoogleUserInput): Promise<StoredUser> {
    // Google-authenticated accounts never use a local password — store an
    // unusable random hash so `passwordHash` stays NOT NULL without adding a
    // login route that could ever match it. emailVerifiedAt is set
    // immediately: Google has already proven ownership of this address, so
    // there's nothing for our own verification email to add.
    const passwordHash = await bcrypt.hash(randomUUID() + randomUUID(), 10);

    const user = await this.prisma.user.create({
      data: {
        email: input.email,
        storeId: this.storeId(),
        passwordHash,
        name: input.name,
        googleId: input.googleId,
        emailVerifiedAt: new Date(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        role: 'customer' as any,
      },
    });

    return this.toStoredUser(user);
  }

  async linkGoogleId(id: string, googleId: string): Promise<StoredUser> {
    // Linking Google to an existing (possibly still-unverified) account is
    // itself proof of ownership — verify it here too, same reasoning as
    // createWithGoogle above, rather than leaving a linked account stuck
    // behind the hard-block login gate.
    const user = await this.prisma.user.update({
      where: { id },
      data: { googleId, emailVerifiedAt: new Date() },
    });
    return this.toStoredUser(user);
  }

  async setResetToken(userId: string, resetTokenHash: string, expiresAt: Date): Promise<void> {
    await this.prisma.user.update({
      where: { id: userId },
      data: { resetTokenHash, resetTokenExpiresAt: expiresAt },
    });
  }

  async findByResetTokenHash(resetTokenHash: string): Promise<StoredUser | undefined> {
    // Deliberately global, not store-scoped like findByEmail/findById — the
    // requester arrives with only a token, no store context to scope by.
    const user = await this.prisma.user.findFirst({
      where: { resetTokenHash, resetTokenExpiresAt: { gt: new Date() } },
    });
    return user ? this.toStoredUser(user) : undefined;
  }

  async resetPassword(userId: string, passwordHash: string): Promise<void> {
    // Single write: sets the new password AND clears both token fields together,
    // so no stale-but-valid token can outlive the password it was meant to reset.
    await this.prisma.user.update({
      where: { id: userId },
      data: { passwordHash, resetTokenHash: null, resetTokenExpiresAt: null },
    });
  }

  async setEmailVerificationToken(userId: string, tokenHash: string, expiresAt: Date): Promise<void> {
    await this.prisma.user.update({
      where: { id: userId },
      data: { emailVerificationTokenHash: tokenHash, emailVerificationTokenExpiresAt: expiresAt },
    });
  }

  async findByEmailVerificationTokenHash(tokenHash: string): Promise<StoredUser | undefined> {
    // Deliberately global, not store-scoped — same reasoning as findByResetTokenHash:
    // the requester arrives with only a token, no store context to scope by.
    const user = await this.prisma.user.findFirst({
      where: { emailVerificationTokenHash: tokenHash, emailVerificationTokenExpiresAt: { gt: new Date() } },
    });
    return user ? this.toStoredUser(user) : undefined;
  }

  async markEmailVerified(userId: string): Promise<void> {
    await this.prisma.user.update({
      where: { id: userId },
      data: {
        emailVerifiedAt: new Date(),
        emailVerificationTokenHash: null,
        emailVerificationTokenExpiresAt: null,
      },
    });
  }

  private toStoredUser(user: {
    id: string;
    email: string;
    passwordHash: string;
    name: string;
    role: UserRole;
    themeMode: ThemeMode;
    storeId?: string | null;
    organizationId?: string | null;
    createdAt: Date;
    emailVerifiedAt?: Date | null;
  }): StoredUser {
    return {
      id: user.id,
      email: user.email,
      passwordHash: user.passwordHash,
      name: user.name,
      role: user.role,
      themeMode: user.themeMode,
      storeId: user.storeId ?? undefined,
      organizationId: user.organizationId ?? undefined,
      createdAt: user.createdAt.toISOString(),
      emailVerifiedAt: user.emailVerifiedAt ? user.emailVerifiedAt.toISOString() : undefined,
    };
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }
}

interface InMemoryUser extends StoredUser {
  googleId?: string;
  resetTokenHash?: string;
  resetTokenExpiresAt?: Date;
  emailVerificationTokenHash?: string;
  emailVerificationTokenExpiresAt?: Date;
}

@Injectable()
export class InMemoryAuthRepository implements AuthRepository {
  private readonly users: InMemoryUser[] = [
    {
      id: 'user-admin',
      email: 'admin@example.com',
      passwordHash: '$2b$10$qTpT5lQZUayGbXCuo1mteuWS.9RZLvRSqPFmCI6VvX0KtF9kT0Z0q',
      name: 'Admin User',
      role: 'admin',
      themeMode: 'light',
      createdAt: '2026-05-01T09:00:00.000Z',
      // Seeded demo account — grandfathered in verified, same as every
      // pre-existing row the real DB migration backfilled.
      emailVerifiedAt: '2026-05-01T09:00:00.000Z',
    },
  ];

  async list(): Promise<StoredUser[]> {
    return this.users.map((user) => ({ ...user }));
  }

  async create(input: CreateUserInput): Promise<StoredUser> {
    const user: StoredUser = {
      id: `user-${String(this.users.length + 1).padStart(3, '0')}`,
      email: input.email,
      passwordHash: input.passwordHash,
      name: input.name,
      role: input.role ?? 'customer',
      organizationId: input.organizationId,
      themeMode: 'light',
      createdAt: new Date().toISOString(),
    };

    this.users.push(user);

    return user;
  }

  async findByEmail(email: string): Promise<StoredUser | undefined> {
    return this.users.find((user) => user.email === email);
  }

  async findById(id: string): Promise<StoredUser | undefined> {
    return this.users.find((user) => user.id === id);
  }

  async updateProfile(id: string, input: UpdateProfileRequest): Promise<StoredUser | undefined> {
    const user = this.users.find((item) => item.id === id);

    if (!user) {
      return undefined;
    }

    user.name = input.name;
    user.email = input.email;

    return user;
  }

  async updateThemeMode(id: string, themeMode: ThemeMode): Promise<StoredUser | undefined> {
    const user = this.users.find((item) => item.id === id);

    if (!user) {
      return undefined;
    }

    user.themeMode = themeMode;

    return user;
  }

  async findByGoogleId(googleId: string): Promise<StoredUser | undefined> {
    return this.users.find((user) => user.googleId === googleId);
  }

  async createWithGoogle(input: CreateGoogleUserInput): Promise<StoredUser> {
    const user: InMemoryUser = {
      id: `user-${String(this.users.length + 1).padStart(3, '0')}`,
      email: input.email,
      passwordHash: await bcrypt.hash(randomUUID() + randomUUID(), 10),
      name: input.name,
      googleId: input.googleId,
      role: 'customer',
      themeMode: 'light',
      createdAt: new Date().toISOString(),
    };

    this.users.push(user);

    return user;
  }

  async linkGoogleId(id: string, googleId: string): Promise<StoredUser> {
    const user = this.users.find((item) => item.id === id);

    if (!user) {
      throw new Error(`User ${id} not found`);
    }

    user.googleId = googleId;

    return user;
  }

  async setResetToken(userId: string, resetTokenHash: string, expiresAt: Date): Promise<void> {
    const user = this.users.find((item) => item.id === userId);

    if (user) {
      user.resetTokenHash = resetTokenHash;
      user.resetTokenExpiresAt = expiresAt;
    }
  }

  async findByResetTokenHash(resetTokenHash: string): Promise<StoredUser | undefined> {
    return this.users.find(
      (user) =>
        user.resetTokenHash === resetTokenHash &&
        !!user.resetTokenExpiresAt &&
        user.resetTokenExpiresAt.getTime() > Date.now()
    );
  }

  async resetPassword(userId: string, passwordHash: string): Promise<void> {
    const user = this.users.find((item) => item.id === userId);

    if (user) {
      user.passwordHash = passwordHash;
      user.resetTokenHash = undefined;
      user.resetTokenExpiresAt = undefined;
    }
  }

  async setEmailVerificationToken(userId: string, tokenHash: string, expiresAt: Date): Promise<void> {
    const user = this.users.find((item) => item.id === userId);

    if (user) {
      user.emailVerificationTokenHash = tokenHash;
      user.emailVerificationTokenExpiresAt = expiresAt;
    }
  }

  async findByEmailVerificationTokenHash(tokenHash: string): Promise<StoredUser | undefined> {
    return this.users.find(
      (user) =>
        user.emailVerificationTokenHash === tokenHash &&
        !!user.emailVerificationTokenExpiresAt &&
        user.emailVerificationTokenExpiresAt.getTime() > Date.now()
    );
  }

  async markEmailVerified(userId: string): Promise<void> {
    const user = this.users.find((item) => item.id === userId);

    if (user) {
      user.emailVerifiedAt = new Date().toISOString();
      user.emailVerificationTokenHash = undefined;
      user.emailVerificationTokenExpiresAt = undefined;
    }
  }
}
